import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ChatAttachment, PASTED_IMAGE_LABEL, formatAttachmentSize } from '../../../models/chat.models';
import { ChatAttachmentService } from '../../../services/chat-attachment.service';
import { AttachmentPreviewDialogComponent } from '../attachment-preview-dialog/attachment-preview-dialog.component';

/**
 * Renders a row of image attachment thumbnails (Issue #1746).
 *
 * The same component serves two places, because the two need to look and behave alike: the composer
 * shows the images a user has attached to the message they are still writing, and a sent user
 * message shows the images it carried. The only difference is whether a remove button is offered,
 * which is what `removable` controls.
 *
 * Every thumbnail is a real button rather than a clickable div, so it is reachable by Tab, activates
 * on Enter and Space without any extra key handling, and is announced with a name that says which
 * image it is and what state it is in. An attachment that failed or expired is drawn differently and
 * says so in its label: the user has to be able to tell "attached" from "not attached" without
 * relying on being able to see the difference.
 *
 * Thumbnails for sent messages are fetched through short-lived signed URLs, requested only when the
 * message is actually rendered. Nothing here holds a durable link to storage.
 */
@Component({
  selector: 'app-attachment-thumbnails',
  imports: [MatButtonModule, MatIconModule, MatProgressBarModule, MatTooltipModule],
  templateUrl: './attachment-thumbnails.component.html',
  styleUrls: ['./attachment-thumbnails.component.scss'],
})
export class AttachmentThumbnailsComponent implements OnChanges, OnDestroy {
  private attachmentService = inject(ChatAttachmentService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  /** Attachments to display, in the order the user added them. */
  @Input() attachments: ChatAttachment[] = [];

  /** Whether each thumbnail offers a remove button. True in the composer, false on a sent message. */
  @Input() removable = false;

  /** Emits the attachment ID the user asked to remove. */
  @Output() removed = new EventEmitter<string>();

  /**
   * Resolved thumbnail URLs, keyed by attachment ID. Drafts already carry a browser-local object
   * URL; sent attachments have one fetched on demand.
   */
  resolvedUrls: Record<string, string | null> = {};

  private destroyed = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attachments']) {
      this.resolveMissingUrls();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  /** File name, or the pasted-image label for an image that came from the clipboard. */
  label(attachment: ChatAttachment): string {
    return attachment.displayName || PASTED_IMAGE_LABEL;
  }

  /** Dimensions and size, shown under the name when they are known. */
  details(attachment: ChatAttachment): string {
    const parts: string[] = [];
    if (attachment.width && attachment.height) {
      parts.push(`${attachment.width} × ${attachment.height}`);
    }
    const size = formatAttachmentSize(attachment.byteSize);
    if (size) parts.push(size);
    return parts.join(' · ');
  }

  /**
   * Accessible name for the thumbnail button.
   *
   * It names the image, says what state it is in, and says what activating the button does, so a
   * screen-reader user is never left guessing whether an image actually attached.
   */
  thumbnailLabel(attachment: ChatAttachment): string {
    const name = this.label(attachment);
    switch (attachment.state) {
      case 'uploading':
        return `${name}, uploading ${attachment.progress ?? 0} percent`;
      case 'validating':
        return `${name}, checking the image`;
      case 'failed':
        return `${name}, not attached. ${attachment.failureReason ?? 'This image could not be attached.'}`;
      case 'expired':
        return `${name}, no longer available`;
      default: {
        const details = this.details(attachment);
        return `${name}${details ? `, ${details}` : ''}. Open a larger preview.`;
      }
    }
  }

  /** Whether this attachment has a usable image to show. */
  hasPreview(attachment: ChatAttachment): boolean {
    return !!(attachment.previewUrl ?? this.resolvedUrls[attachment.attachmentId]);
  }

  previewUrl(attachment: ChatAttachment): string | null {
    return attachment.previewUrl ?? this.resolvedUrls[attachment.attachmentId] ?? null;
  }

  /** Whether the attachment is in a state the user needs told about. */
  isUnavailable(attachment: ChatAttachment): boolean {
    return attachment.state === 'failed' || attachment.state === 'expired';
  }

  /** Opens the larger preview. A thumbnail that has nothing to show explains itself instead. */
  openPreview(attachment: ChatAttachment): void {
    this.dialog.open(AttachmentPreviewDialogComponent, {
      data: { attachment, previewUrl: this.previewUrl(attachment) },
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: 'dialog',
      restoreFocus: true,
      ariaLabel: `Preview of ${this.label(attachment)}`,
    });
  }

  /** "Page 3 · A-201.pdf" for a Preview region crop (Issue #1747). */
  sourceLabel(attachment: ChatAttachment): string {
    const region = attachment.pageRegion;
    if (!region) return '';
    const base = region.label || `Page ${region.pageNumber}${region.fileName ? ` · ${region.fileName}` : ''}`;
    return region.description ? `${base} · ${region.description}` : base;
  }

  /**
   * Short visible text for the source link, e.g. "Page 1 · CE-4" (UX review of Issue #1747).
   *
   * The chip is narrow, so the link shows the page and the sheet number when known, or the page and
   * the file name otherwise; the full label is in the tooltip and the accessible name.
   */
  sourceLinkText(attachment: ChatAttachment): string {
    const region = attachment.pageRegion;
    if (!region) return '';
    const sheet = (region.description ?? '').split(' · ')[0].trim();
    const detail = sheet || region.fileName;
    return detail ? `Page ${region.pageNumber} · ${detail}` : `Page ${region.pageNumber}`;
  }

  /** Full accessible name for the source link: page, file, sheet and what activating it does. */
  sourceAriaLabel(attachment: ChatAttachment): string {
    const region = attachment.pageRegion;
    if (!region) return '';
    const file = region.fileName ? ` of ${region.fileName}` : '';
    const sheet = region.description ? `, sheet ${region.description}` : '';
    return `Show the selected area on page ${region.pageNumber}${file}${sheet} in the preview`;
  }

  /**
   * Returns to the page a region crop came from and outlines the region again (Issue #1747).
   *
   * Only the attachment ID travels in the URL. The Preview asks the backend for the stored source,
   * which re-checks ownership and project access and reports whether the page has changed since.
   */
  openSource(attachment: ChatAttachment, event: Event): void {
    event.stopPropagation();
    const region = attachment.pageRegion;
    if (!region?.projectId) return;
    void this.router.navigate(
      [
        '/projects',
        region.projectId,
        'files',
        region.fileId || '1',
        'pages',
        String(region.pageNumber || 1),
        'preview',
      ],
      { queryParams: { chatRegion: attachment.attachmentId } }
    );
  }

  remove(attachment: ChatAttachment, event: Event): void {
    // The remove button sits inside the thumbnail button's row; stop the click from also opening
    // the preview the user did not ask for.
    event.stopPropagation();
    this.removed.emit(attachment.attachmentId);
  }

  /**
   * Fetches signed URLs for any sent attachment that does not already have one.
   *
   * A failed lookup leaves the entry as null rather than retrying in a loop, and the thumbnail then
   * shows the unavailable state. Results are cached in the service, so re-rendering a long
   * conversation does not mint a fresh URL per thumbnail per repaint.
   */
  private resolveMissingUrls(): void {
    for (const attachment of this.attachments) {
      if (attachment.previewUrl) continue;
      if (attachment.state !== 'ready') continue;
      if (attachment.attachmentId in this.resolvedUrls) continue;
      this.resolvedUrls[attachment.attachmentId] = null;
      void this.attachmentService.viewUrl(attachment.attachmentId).then(url => {
        if (this.destroyed) return;
        this.resolvedUrls = { ...this.resolvedUrls, [attachment.attachmentId]: url };
        // The host renders with OnPush, so assigning here is not enough: the resolved URL would
        // sit in the field and never reach the template. The symptom was a permanent placeholder
        // glyph on a message whose image had in fact been fetched successfully — clicking the
        // thumbnail made it appear, because the click marked the view dirty, with no second
        // request. Nothing about the data was wrong; only the notification was missing.
        this.cdr.markForCheck();
      });
    }
  }
}
